/**
 * Estado dos filtros do modal SeletorConjuntos.
 *
 * Espelha `useFiltrosUsinas.ts`:
 *   - busca textual via `useDeferredValue` (React 19) — input reflete
 *     imediato, propagacao para o filtro adia sob pressao do reconciler.
 *   - multi-selects (UF, Subsistema SM, Subsistema SH, Localizacao EPE)
 *     como `string[]`. Vazio = sem filtro.
 *
 * Nao ha range numerico (potencia) no shape de conjuntos — o agregado
 * mostra `potencia_total_mw` somente como coluna ordenavel, nao filtravel.
 */
import {
  Dispatch, SetStateAction, useDeferredValue, useMemo, useState,
} from 'react';
import { FiltrosConjuntos } from 'helper/windx';

export interface UseFiltrosConjuntosReturn {
  filtros: FiltrosConjuntos;
  // `busca` deferida — usar no helper de filtragem, NAO no input
  filtrosEfetivos: FiltrosConjuntos;
  setBusca: Dispatch<SetStateAction<string>>;
  setUfs: Dispatch<SetStateAction<string[]>>;
  setSubsistemasSm: Dispatch<SetStateAction<string[]>>;
  setSubsistemasSh: Dispatch<SetStateAction<string[]>>;
  setLocalizacoesEpe: Dispatch<SetStateAction<string[]>>;
  limparFiltros: () => void;
  temFiltrosAtivos: boolean;
}

const FILTROS_VAZIOS: FiltrosConjuntos = {
  busca: '',
  ufs: [],
  subsistemasSm: [],
  subsistemasSh: [],
  localizacoesEpe: [],
};

export default function useFiltrosConjuntos(): UseFiltrosConjuntosReturn {
  const [busca, setBusca] = useState('');
  const [ufs, setUfs] = useState<string[]>([]);
  const [subsistemasSm, setSubsistemasSm] = useState<string[]>([]);
  const [subsistemasSh, setSubsistemasSh] = useState<string[]>([]);
  const [localizacoesEpe, setLocalizacoesEpe] = useState<string[]>([]);

  const buscaDeferida = useDeferredValue(busca);

  const filtros: FiltrosConjuntos = useMemo(
    () => ({
      busca, ufs, subsistemasSm, subsistemasSh, localizacoesEpe,
    }),
    [busca, ufs, subsistemasSm, subsistemasSh, localizacoesEpe],
  );

  const filtrosEfetivos: FiltrosConjuntos = useMemo(
    () => ({
      busca: buscaDeferida,
      ufs,
      subsistemasSm,
      subsistemasSh,
      localizacoesEpe,
    }),
    [buscaDeferida, ufs, subsistemasSm, subsistemasSh, localizacoesEpe],
  );

  const temFiltrosAtivos = useMemo(
    () => (
      busca.trim().length > 0
      || ufs.length > 0
      || subsistemasSm.length > 0
      || subsistemasSh.length > 0
      || localizacoesEpe.length > 0
    ),
    [busca, ufs, subsistemasSm, subsistemasSh, localizacoesEpe],
  );

  const limparFiltros = () => {
    setBusca(FILTROS_VAZIOS.busca);
    setUfs(FILTROS_VAZIOS.ufs);
    setSubsistemasSm(FILTROS_VAZIOS.subsistemasSm);
    setSubsistemasSh(FILTROS_VAZIOS.subsistemasSh);
    setLocalizacoesEpe(FILTROS_VAZIOS.localizacoesEpe);
  };

  return {
    filtros,
    filtrosEfetivos,
    setBusca,
    setUfs,
    setSubsistemasSm,
    setSubsistemasSh,
    setLocalizacoesEpe,
    limparFiltros,
    temFiltrosAtivos,
  };
}
